import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Gift, 
  Sparkles, 
  Star, 
  CheckCircle2, 
  Lock, 
  Zap, 
  Trophy,
  Coins,
  RefreshCw
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { UserModelData } from "../../types";
import useThemeHook from "../hooks/useThemeHook";
import arthereumProvider from "../../backend/ArthereumProvider";
import { actions } from "../redux";
import emitter, { EmitterEvents } from "../utils/eventsEmitter";

interface DailyRewardModalProps {
  onClose: () => void;
}

const DailyRewardModal: React.FC<DailyRewardModalProps> = ({ onClose }) => {
  const theme = useThemeHook();
  const dispatch = useDispatch();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;
  
  const [claiming, setClaiming] = useState(false);
  const [success, setSuccess] = useState(false);
  const [claimedToday, setClaimedToday] = useState(false);

  const streak = user?.currentStreak || 1;
  const currentDayIndex = (streak - 1) % 7; // 0 to 6

  useEffect(() => {
    // Check if reward was already claimed today
    if (user?.lastLoginRewardDate) {
      const lastClaim = new Date(user.lastLoginRewardDate.seconds * 1000);
      const today = new Date();
      if (lastClaim.toDateString() === today.toDateString()) {
        setClaimedToday(true);
      }
    }
  }, [user?.lastLoginRewardDate]);

  const rewards = arthereumProvider.DAILY_REWARDS;

  const handleClaim = async () => {
    if (claiming || claimedToday || success) return;

    setClaiming(true);
    try {
      const updatedModel = await arthereumProvider.claimDailyReward(user!.id);
      dispatch(actions.setActualUser(updatedModel));
      
      // Contest points for login were removed from the system.

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error: any) {
      alert(error.message || "Failed to claim reward");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className={`w-full max-w-sm rounded-[40px] overflow-hidden shadow-2xl ${
          theme === "dark" ? "bg-slate-900 border border-slate-800" : "bg-white"
        }`}
      >
        {/* Header Section */}
        <div className="relative h-40 flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-indigo-600 to-purple-700 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-[-10%] left-[-10%] w-40 h-40 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-[-10%] right-[-10%] w-40 h-40 bg-white rounded-full blur-3xl" />
          </div>
          
          <motion.div
            initial={{ rotate: -15, scale: 0 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-3 backdrop-blur-xl"
          >
            <Gift className="text-white" size={32} />
          </motion.div>
          
          <h2 className="text-2xl font-black text-white leading-tight">Daily Rewards</h2>
          <p className="text-indigo-100 text-xs font-bold uppercase tracking-widest mt-1">
            Day {currentDayIndex + 1} of 7
          </p>
        </div>

        {/* Reward Grid */}
        <div className="p-6">
          <div className="grid grid-cols-4 gap-2 mb-8">
            {rewards.map((reward, index) => {
              const isToday = index === currentDayIndex;
              const isClaimed = index < currentDayIndex || (index === currentDayIndex && claimedToday);
              const isLocked = index > currentDayIndex;

              return (
                <div
                  key={index}
                  className={`
                    flex flex-col items-center justify-center p-2 rounded-2xl border-2 transition-all
                    ${index === 6 ? 'col-span-2' : ''}
                    ${isToday && !claimedToday 
                      ? "bg-indigo-600/10 border-indigo-600 animate-pulse" 
                      : isClaimed 
                        ? "bg-emerald-500/10 border-emerald-500/50" 
                        : "bg-slate-100 border-slate-200 dark:bg-slate-800 dark:border-slate-800"
                    }
                  `}
                >
                  <span className={`text-[8px] font-black uppercase mb-1 ${isClaimed ? "text-emerald-500" : isToday ? "text-indigo-600" : "text-slate-400"}`}>
                    Day {index + 1}
                  </span>
                  
                  <div className="my-1">
                    {isClaimed ? (
                      <CheckCircle2 className="text-emerald-500" size={20} />
                    ) : index === 6 ? (
                      <div className="relative">
                         <Gift className={isToday ? "text-indigo-600" : "text-slate-300"} size={24} />
                         <Star className="absolute -top-1 -right-1 text-yellow-400 fill-yellow-400" size={10} />
                      </div>
                    ) : reward.extraSpin ? (
                      <RefreshCw className={isToday ? "text-indigo-600" : "text-slate-300"} size={20} />
                    ) : (
                      <Coins className={isToday ? "text-indigo-600" : "text-slate-300"} size={18} />
                    )}
                  </div>
                  
                  <span className={`text-[9px] font-bold ${isClaimed || isToday ? theme === "dark" ? "text-white" : "text-slate-900" : "text-slate-400"}`}>
                    {reward.ath} ATH
                  </span>
                </div>
              );
            })}
          </div>

          {/* Action Button Area */}
          <div className="space-y-4">
            {success ? (
               <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-emerald-500 py-4 rounded-3xl flex items-center justify-center gap-3 shadow-xl shadow-emerald-500/20"
               >
                 <Trophy className="text-white" size={24} />
                 <span className="text-white font-black">Reward Claimed! 🏆</span>
               </motion.div>
            ) : claimedToday ? (
              <div className="text-center">
                 <p className={`text-sm font-bold ${theme === "dark" ? "text-slate-400" : "text-gray-500"} mb-2`}>
                   Come back tomorrow for your next reward!
                 </p>
                 <button
                   onClick={onClose}
                   className={`w-full py-4 rounded-3xl font-black ${theme === "dark" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-900"}`}
                 >
                   Got it
                 </button>
              </div>
            ) : (
              <button
                onClick={handleClaim}
                disabled={claiming}
                className={`
                  w-full py-5 rounded-3xl font-black text-lg shadow-xl transition-all transform active:scale-95
                  bg-gradient-to-r from-indigo-600 to-purple-600 text-white
                  hover:shadow-indigo-500/30
                `}
              >
                {claiming ? "Claiming..." : "Claim Reward 🔥"}
              </button>
            )}
            
            {!success && !claimedToday && (
              <p className={`text-[10px] text-center font-bold uppercase tracking-tighter ${theme === "dark" ? "text-slate-500" : "text-gray-400"}`}>
                Streak resets if you miss a day!
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DailyRewardModal;
